// src/pages/ResetPassword.jsx
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import backgroundImage from '../assets/images/pexels-pnw-prod-8251001.jpg';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formVisible, setFormVisible] = useState(true);

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (newPassword !== confirmPassword) {
      return setError("Passwords don't match");
    }

    if (!validatePassword(newPassword)) {
      return setError(
        'Password must be 8+ characters with uppercase, lowercase, number, and special character'
      );
    }

    setIsSubmitting(true);

    try {
      const res = await fetch('https://app.writeapp.in/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage('✅ Password reset successfully. You may now login.');
        setFormVisible(false);
      } else {
        setError(data.message || 'Invalid or expired token.');
      }
    } catch {
      setError('Server error. Try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-6 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={backgroundImage} alt="Background" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-dark-gray/30 backdrop-blur-md border border-gray-800/50 rounded-xl shadow-2xl z-10 p-6 sm:p-8"
      >
        <h1 className="text-2xl font-bold text-white mb-6 text-center">Reset Password</h1>

        {message && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-200 px-4 py-3 rounded-md mb-4">
            {message}
          </div>
        )}

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {formVisible && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-300 mb-2 font-medium">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-dark-gray/50 border border-gray-700/50 rounded-lg px-4 py-3 text-white"
                placeholder="Enter new password"
                required
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2 font-medium">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-dark-gray/50 border border-gray-700/50 rounded-lg px-4 py-3 text-white"
                placeholder="Confirm new password"
                required
              />
            </div>

            <motion.button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white py-3 rounded-lg transition-all duration-300 flex items-center justify-center font-medium shadow-lg ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-blue-500/20'
              }`}
            >
              {isSubmitting ? 'Resetting...' : 'Reset Password'}
            </motion.button>
          </form>
        )}

        {/* Back Button */}
        {!formVisible && (
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-block px-5 py-2 rounded-md text-white bg-gray-800 hover:bg-gray-700 transition"
            >
              Back to Home
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ResetPassword;
